import { useCallback, useMemo } from 'react';
import { computeFinanceKpis, type FinanceGateway, type IncomingInvoiceGateway } from '@forq/finance-data';
import type { SalesGateway } from '@forq/sales-data';
import type { WarehouseGateway } from '@forq/warehouse-data';
import type { ServiceGateway } from '@forq/service-data';
import type { RentalGateway } from '@forq/rental-data';
import type { MachinesGateway } from '@forq/machines-data';
import { CustomizableMetrics } from '@forq/ui';
import { financeRangeOf, type ResolvedPeriod } from './period';
import { ReportActions, type SectionReport } from './report';
import { euro0, Kpi, KpiGrid, num, SectionShell, useAsyncData } from './shared';

interface OverviewData {
  readonly sales: { readonly pipelineValue: number; readonly openOpportunities: number; readonly wonValueThisMonth: number } | null;
  readonly finance: { readonly receivablesOpen: number; readonly revenue: number; readonly grossProfit: number } | null;
  readonly warehouse: { readonly lowStockItems: number; readonly stockedItems: number } | null;
  readonly machines: { readonly count: number; readonly value: number } | null;
  readonly service: { readonly billingReadyCount: number; readonly approvalPendingCount: number } | null;
  readonly rental: number | null;
}

/**
 * Modulübergreifende Übersicht: je Bereich eine Leitkennzahl. Der Zeitraum wirkt auf die Fluss-Zahl
 * (Finanz-Umsatz); die übrigen sind aktuelle Stände. Jeder Block nur bei Sichtrecht, fehlerrobust.
 */
export function OverviewSection({
  tenantId, tenantName, period, permissions,
  salesGateway, financeGateway, incomingGateway, warehouseGateway, serviceGateway, rentalGateway, machinesGateway,
}: {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly period: ResolvedPeriod;
  readonly permissions: ReadonlySet<string>;
  readonly salesGateway: SalesGateway;
  readonly financeGateway: FinanceGateway;
  readonly incomingGateway: IncomingInvoiceGateway;
  readonly warehouseGateway: WarehouseGateway;
  readonly serviceGateway: ServiceGateway;
  readonly rentalGateway: RentalGateway;
  readonly machinesGateway: MachinesGateway;
}) {
  const load = useCallback(async (): Promise<OverviewData> => {
    const can = (permission: string) => permissions.has(permission);
    const [sales, finance, warehouse, machines, service, rental] = await Promise.all([
      can('sales.view')
        ? salesGateway.getOverview(tenantId).then((o) => ({ pipelineValue: o.pipelineValue, openOpportunities: o.openOpportunities, wonValueThisMonth: o.wonValueThisMonth })).catch(() => null)
        : Promise.resolve(null),
      can('finance.view')
        ? Promise.all([financeGateway.listInvoices({ tenantId }), incomingGateway.listIncomingInvoices({ tenantId })])
            .then(([invoices, incoming]) => { const k = computeFinanceKpis(invoices, incoming, financeRangeOf(period)); return { receivablesOpen: k.receivablesOpen, revenue: k.revenue, grossProfit: k.grossProfit }; })
            .catch(() => null)
        : Promise.resolve(null),
      can('warehouse.view')
        ? warehouseGateway.getOverview(tenantId).then((o) => ({ lowStockItems: o.lowStockItems, stockedItems: o.stockedItems })).catch(() => null)
        : Promise.resolve(null),
      can('machines.view')
        ? machinesGateway.searchMachines({ tenantId, status: 'stock', pageSize: 1000 }).then((p) => ({ count: p.total, value: p.items.reduce((sum, m) => sum + m.salesPrice, 0) })).catch(() => null)
        : Promise.resolve(null),
      can('service.view')
        ? serviceGateway.getOverview(tenantId).then((o) => ({ billingReadyCount: o.billingReadyCount, approvalPendingCount: o.approvalPendingCount })).catch(() => null)
        : Promise.resolve(null),
      can('rental.view')
        ? rentalGateway.listRentalOrders(tenantId).then((orders) => orders.filter((order) => order.status === 'active').length).catch(() => null)
        : Promise.resolve(null),
    ]);
    return { sales, finance, warehouse, machines, service, rental };
  }, [permissions, tenantId, period, salesGateway, financeGateway, incomingGateway, warehouseGateway, serviceGateway, rentalGateway, machinesGateway]);
  const { data, loading, error, reload } = useAsyncData(load);

  const report: SectionReport | null = useMemo(() => {
    if (!data) return null;
    const kpis: { label: string; value: string }[] = [];
    if (data.sales) kpis.push({ label: 'Pipeline offen', value: euro0(data.sales.pipelineValue) });
    if (data.finance) { kpis.push({ label: `Umsatz (${period.label})`, value: euro0(data.finance.revenue) }); kpis.push({ label: `Rohertrag (${period.label})`, value: euro0(data.finance.grossProfit) }); kpis.push({ label: 'Offene Forderungen', value: euro0(data.finance.receivablesOpen) }); }
    if (data.machines) kpis.push({ label: 'Lagerwert Maschinen', value: euro0(data.machines.value) });
    if (data.rental != null) kpis.push({ label: 'Aktive Vermietungen', value: num(data.rental) });
    if (data.warehouse) kpis.push({ label: 'Unter Mindestbestand', value: num(data.warehouse.lowStockItems) });
    if (data.service) kpis.push({ label: 'Service abrechnungsreif', value: num(data.service.billingReadyCount) });
    return { title: 'Übersicht', kpis, blocks: [] };
  }, [data, period]);

  return (
    <SectionShell loading={loading} error={error} onReload={reload}>
      {data && report ? (
        <div className="auswertung__stack">
          <div className="auswertung__sectionhead">
            <span className="sales-eyebrow">Übersicht · Umsatz im Zeitraum {period.label}, sonst aktueller Stand</span>
            <ReportActions report={report} period={period} tenantName={tenantName} />
          </div>
          <p className="auswertung__lead">Die wichtigsten Zahlen aller Bereiche auf einen Blick. Details in den einzelnen Reitern.</p>
          <KpiGrid>
            <CustomizableMetrics
              pageKey="auswertung-overview"
              tiles={[
                ...(data.sales ? [{ key: 'sales-pipeline', node: <Kpi label="Pipeline offen" value={euro0(data.sales.pipelineValue)} hint={`${num(data.sales.openOpportunities)} Chancen · ${euro0(data.sales.wonValueThisMonth)} gewonnen (Monat)`} /> }] : []),
                ...(data.finance ? [{ key: 'fin-revenue', node: <Kpi label={`Umsatz (${period.label})`} value={euro0(data.finance.revenue)} hint={`Offene Forderungen ${euro0(data.finance.receivablesOpen)}`} /> }] : []),
                ...(data.finance ? [{ key: 'fin-grossprofit', node: <Kpi label={`Rohertrag (${period.label})`} value={euro0(data.finance.grossProfit)} hint="Umsatz − Wareneinkauf" /> }] : []),
                ...(data.machines ? [{ key: 'machines-value', node: <Kpi label="Lagerwert Maschinen" value={euro0(data.machines.value)} hint={`${num(data.machines.count)} verkaufbar`} /> }] : []),
                ...(data.rental != null ? [{ key: 'rental-active', node: <Kpi label="Aktive Vermietungen" value={num(data.rental)} hint="Mietpark" /> }] : []),
                ...(data.warehouse ? [{ key: 'wh-lowstock', node: <Kpi label="Unter Mindestbestand" value={num(data.warehouse.lowStockItems)} hint={`${num(data.warehouse.stockedItems)} Artikel mit Bestand`} /> }] : []),
                ...(data.service ? [{ key: 'svc-billingready', node: <Kpi label="Service abrechnungsreif" value={num(data.service.billingReadyCount)} hint={`${num(data.service.approvalPendingCount)} Freigaben offen`} /> }] : []),
              ]}
            />
          </KpiGrid>
        </div>
      ) : null}
    </SectionShell>
  );
}
